/**
 * Scene layers for the starmap.
 *
 * Three layers that coexist in one galactic coordinate space rather than three
 * screens that replace each other: the galaxy point cloud is always present,
 * a cluster's stars resolve on top of it when close, and a system's contents
 * resolve on top of that. `lod.ts` decides which are live.
 *
 * Favours legibility over looking like the finished game — positions are used
 * exactly as generated, never nudged to look better, or the map would stop
 * telling the truth about the generator.
 */

import {
  getClusterSummaries,
  getStationGeometry,
  STATION_SCALE,
  getSystem,
  orbitPath,
  positionAt,
  hashSeed,
  PAD_LABELS,
  SIZE_CLASSES,
  planetPosition,
  type Cluster,
  type Orbit,
  type FactionId,
  type SystemSummary,
  type Vec2,
} from '@interspacial/shared';
import * as THREE from 'three';

import { convexHull, expandHull } from './hull';
import { createPlanetMaterial } from './shaders/planet';
import { createSunGlowMaterial, createSunMaterial } from './shaders/sun';
import { SYSTEM_RENDER_SCALE } from './lod';
import { factionTexture } from './factionIcons';

/**
 * How much bigger than life a station is drawn on the map.
 *
 * Undoing the whole of `STATION_SCALE` put decks back at map-legible size but
 * overstated them — a station is not a rival to a planet and should not look
 * like one even on a chart. Half of that is still comfortably readable.
 */
const MAP_STATION_EXAGGERATION = 0.5;

export interface Pickable {
  object: THREE.Object3D;
  hover: string;
}

export interface LayerLabel {
  text: string;
  position: THREE.Vector3;
  muted?: boolean;
  /**
   * Live world position, for labels attached to something that moves. Static
   * labels omit it and `position` is used as-is.
   */
  follow?: () => THREE.Vector3;
}

export interface Layer {
  group: THREE.Group;
  pickables: Pickable[];
  labels: LayerLabel[];
  /**
   * The system's own bodies, by name, and the frame they live in.
   *
   * Only the system layer has these. It is what lets the map put a marker where
   * the *ship* is: a saved position is an offset from whichever body had hold of
   * it, so drawing it means finding that body here and working outward from it.
   */
  bodies?: {
    frame: THREE.Object3D;
    byName: Map<string, THREE.Object3D>;
  };
  /** Advance anything that moves. Only the system layer has anything to do. */
  animate?: (seconds: number) => void;
  dispose: () => void;
}

export const ALLEGIANCE_COLORS: Record<FactionId | 'unaligned', number> = {
  red: 0xff5f6b,
  blue: 0x5b9dff,
  green: 0x55cc7a,
  yellow: 0xffc94a,
  turquoise: 0x3fd9c8,
  rogue: 0xff2d2d,
  unaligned: 0x8b93a3,
};

// ─── Disposal ────────────────────────────────────────────────────────────────

/**
 * Layers are built and thrown away constantly as the camera moves, and Three
 * does not free GPU buffers on garbage collection — leaking them would bleed
 * memory the whole time the map is open.
 */
function disposeGroup(group: THREE.Group): void {
  group.traverse((object) => {
    const withGeometry = object as Partial<THREE.Mesh>;
    withGeometry.geometry?.dispose();

    const material = withGeometry.material;
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else material?.dispose();
  });
}

function layerOf(
  group: THREE.Group,
  pickables: Pickable[] = [],
  labels: LayerLabel[] = [],
): Layer {
  return { group, pickables, labels, dispose: () => disposeGroup(group) };
}

// ─── Primitives ──────────────────────────────────────────────────────────────

function lineLoop(points: readonly THREE.Vector3[], color: number, opacity: number): THREE.LineLoop {
  return new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([...points]),
    new THREE.LineBasicMaterial({ color, transparent: true, opacity }),
  );
}

function hullLoop(points: readonly Vec2[], z: number, color: number, opacity: number): THREE.LineLoop {
  return lineLoop(points.map((p) => new THREE.Vector3(p.x, p.y, z)), color, opacity);
}

/**
 * A soft round dot.
 *
 * PointsMaterial draws hard squares by default, which read as tiny boxes
 * floating over the cluster spheres rather than as distant stars. Built once
 * and shared by every point in the cloud.
 */
let discTexture: THREE.CanvasTexture | null = null;
function starDotTexture(): THREE.CanvasTexture {
  if (discTexture) return discTexture;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.45, 'rgba(255,255,255,0.85)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  discTexture = new THREE.CanvasTexture(canvas);
  return discTexture;
}

/** Invisible generous hit target, so small markers stay clickable. */
function hitArea(radius: number): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.SphereGeometry(radius, 8, 6),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
}

// ─── Galaxy ──────────────────────────────────────────────────────────────────

export interface GalaxyLayer extends Layer {
  /** Per-cluster hull loops, so they can be faded as one. */
  hulls: THREE.LineLoop[];
  points: THREE.Points;
  /**
   * Suppress a single cluster's stars and outline, because another layer is
   * drawing them properly. Everything else in the galaxy stays visible, so the
   * cluster you are in keeps its context instead of floating in a void.
   */
  setClusterHidden: (clusterId: string | null) => void;
}

/**
 * Every system in the galaxy as one point cloud, plus a hull outline per
 * cluster.
 *
 * One merged cloud rather than a mesh per system: at twelve thousand systems,
 * individual meshes would be a draw call apiece for no benefit.
 */
export function buildGalaxyLayer(map: readonly { cluster: Cluster; systems: readonly SystemSummary[] }[]): GalaxyLayer {
  const group = new THREE.Group();
  const pickables: Pickable[] = [];
  const hulls: THREE.LineLoop[] = [];

  const positions: number[] = [];
  const colors: number[] = [];
  const scratch = new THREE.Color();

  // Where each cluster's vertices live in the merged buffer, so one cluster can
  // be blanked without splitting the cloud into 500 draw calls.
  const ranges = new Map<string, { start: number; count: number }>();

  for (const { cluster, systems } of map) {
    const footprint: Vec2[] = [];
    let dockable = 0;
    const start = positions.length / 3;

    for (const system of systems) {
      const { x, y, z } = system.galacticPosition;
      positions.push(x, y, z);
      footprint.push({ x, y });

      scratch.set(system.starColor);
      const lit = system.stationCount > 0 ? 1 : 0.4;
      colors.push(scratch.r * lit, scratch.g * lit, scratch.b * lit);
      if (system.stationCount > 0) dockable++;
    }

    ranges.set(cluster.id, { start, count: systems.length });

    // A line around the collection itself, from the systems' own footprint
    // rather than an idealised circle, so a lopsided cluster looks lopsided.
    const hull = hullLoop(expandHull(convexHull(footprint), 300), cluster.position.z, 0x4d86c4, 0.3);
    hulls.push(hull);
    group.add(hull);

    const hit = hitArea(Math.max(cluster.radius, 2400));
    hit.position.set(cluster.position.x, cluster.position.y, cluster.position.z);
    group.add(hit);
    pickables.push({
      object: hit,
      hover: `${cluster.name} — ${systems.length} systems, ${dockable} dockable`,
    });
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const points = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 520,
      vertexColors: true,
      sizeAttenuation: true,
      transparent: true,
      map: starDotTexture(),
      // Additive so overlapping distant stars pile into brighter regions,
      // and depthWrite off so they never punch holes in each other.
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  group.add(points);

  // The pristine colours, so a blanked cluster can be restored exactly.
  const baseColors = Float32Array.from(colors);
  const colorAttribute = geometry.getAttribute('color') as THREE.BufferAttribute;
  let hiddenCluster: string | null = null;

  const setClusterHidden = (clusterId: string | null): void => {
    if (clusterId === hiddenCluster) return;

    // Restore whatever was blanked last time.
    if (hiddenCluster) {
      const previous = ranges.get(hiddenCluster);
      if (previous) {
        const from = previous.start * 3;
        const to = from + previous.count * 3;
        (colorAttribute.array as Float32Array).set(baseColors.subarray(from, to), from);
      }
    }

    hiddenCluster = clusterId;

    if (clusterId) {
      const range = ranges.get(clusterId);
      if (range) {
        // Black, not removed: the material is additive, so black contributes
        // nothing and the buffer keeps its shape.
        (colorAttribute.array as Float32Array).fill(0, range.start * 3, (range.start + range.count) * 3);
      }
      // The outline deliberately stays. Only the *stars* are duplicated by the
      // cluster layer; the boundary is the one piece of context that says how
      // far the cluster reaches, which matters most once you are inside it.
    }

    colorAttribute.needsUpdate = true;
  };

  return { ...layerOf(group, pickables), hulls, points, setClusterHidden };
}

// ─── Cluster ─────────────────────────────────────────────────────────────────

/** One cluster's systems, resolved as stars in galactic space. */
export function buildClusterLayer(seed: string, cluster: Cluster): Layer {
  const group = new THREE.Group();
  const pickables: Pickable[] = [];
  const labels: LayerLabel[] = [];

  const systems = getClusterSummaries(seed, cluster.id);
  const starGeometry = new THREE.SphereGeometry(1, 12, 10);

  for (const system of systems) {
    const { x, y, z } = system.galacticPosition;

    const star = new THREE.Mesh(
      starGeometry,
      new THREE.MeshBasicMaterial({ color: new THREE.Color(system.starColor) }),
    );
    star.position.set(x, y, z);
    star.scale.setScalar(22 + system.starRadius * 0.16);
    // Tagged so the marker can be removed once the real system is drawn in its
    // place — otherwise the system's own star sits inside this placeholder ball.
    star.userData['systemId'] = system.id;
    group.add(star);

    // A dropline to the cluster's mid-plane, so height reads as height rather
    // than as distance from the camera.
    const stalk = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(x, y, cluster.position.z),
        new THREE.Vector3(x, y, z),
      ]),
      new THREE.LineBasicMaterial({ color: 0x4d86c4, transparent: true, opacity: 0.2 }),
    );
    stalk.userData['systemId'] = system.id;
    group.add(stalk);

    const hit = hitArea(120);
    hit.position.set(x, y, z);
    group.add(hit);
    pickables.push({
      object: hit,
      hover: `${system.name} — ${system.planetCount}p ${system.stationCount}st ${system.asteroidFieldCount}f ${system.anomalyCount}a`,
    });

    labels.push({
      text: system.name,
      position: new THREE.Vector3(x, y, z + 110),
      muted:
        system.planetCount +
          system.stationCount +
          system.asteroidFieldCount +
          system.anomalyCount ===
        0,
    });
  }

  return layerOf(group, pickables, labels);
}

// ─── System ──────────────────────────────────────────────────────────────────

/**
 * One system's contents, placed at its galactic position and shrunk by
 * SYSTEM_RENDER_SCALE so it fits between its neighbours.
 *
 * Everything here orbits something, so nothing has a fixed position: the layer
 * exposes `animate(seconds)`, which re-evaluates every orbit at that moment.
 * Bodies orbiting a planet are parented to it in the scene graph, so a moon
 * follows its planet for free rather than needing the planet's motion folded
 * into its own.
 */
export function buildSystemLayer(seed: string, summary: SystemSummary): Layer {
  const outer = new THREE.Group();
  outer.position.set(
    summary.galacticPosition.x,
    summary.galacticPosition.y,
    summary.galacticPosition.z,
  );
  outer.scale.setScalar(SYSTEM_RENDER_SCALE);

  const group = new THREE.Group();
  outer.add(group);

  const pickables: Pickable[] = [];
  const system = getSystem(seed, summary.id);

  /** Labels attach to a moving node and are resolved from its world position. */
  const labelTargets: { text: string; node: THREE.Object3D; lift: number; muted?: boolean }[] = [];

  /**
   * Bodies under their plain names.
   *
   * Kept separately from the labels, which are decorated — a station's label
   * carries its pad classes and an anomaly's carries its kind. The flight view
   * saves the ship's position against a *plain* name, so matching against the
   * decorated ones would silently never resolve.
   */
  const bodyNodes = new Map<string, THREE.Object3D>();

  // ── Star ─────────────────────────────────────────────────────────────────
  const sunMaterial = createSunMaterial({
    color: system.starColor,
    seed: hashSeed(system.id),
    quality: 'low',
  });
  const star = new THREE.Mesh(new THREE.SphereGeometry(system.starRadius, 48, 32), sunMaterial);
  group.add(star);

  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(system.starRadius * 2.2, 24, 18),
    createSunGlowMaterial(system.starColor),
  );
  group.add(glow);

  // Everything is lit from the star, which sits at the system's own origin.
  const lightPosition = new THREE.Vector3(
    summary.galacticPosition.x,
    summary.galacticPosition.y,
    summary.galacticPosition.z,
  );
  /** Materials that need the clock. */
  const animatedMaterials: THREE.ShaderMaterial[] = [sunMaterial];

  // Everything that moves, re-evaluated each frame from its orbit. Bodies with
  // a `parent` group are positioned relative to it, so a moon inherits its
  // planet's motion instead of having to reproduce it.
  const moving: { node: THREE.Object3D; orbit: Orbit }[] = [];
  const track = (node: THREE.Object3D, orbit: Orbit, parent: THREE.Object3D = group) => {
    parent.add(node);
    moving.push({ node, orbit });
  };

  /** Orbit paths are drawn in the frame of whatever the body goes around. */
  const drawOrbitPath = (orbit: Orbit, parent: THREE.Object3D, opacity: number) => {
    parent.add(
      lineLoop(
        orbitPath(orbit, 128).map((p) => new THREE.Vector3(p.x, p.y, p.z)),
        0x6f8fae,
        opacity,
      ),
    );
  };

  // A pivot per planet: children ride along, and it is what stations, fields
  // and moons attach to.
  const planetPivots = new Map<string, THREE.Group>();

  for (const planet of system.planets) {
    drawOrbitPath(planet.orbit, group, 0.22);

    const pivot = new THREE.Group();
    planetPivots.set(planet.id, pivot);
    track(pivot, planet.orbit);

    const planetMaterial = createPlanetMaterial({
      type: planet.type,
      baseColor: planet.baseColor,
      accentColor: planet.accentColor,
      highlightColor: planet.highlightColor,
      seed: planet.shaderSeed,
      capExtent: planet.capExtent,
      lightPosition,
      lightColor: system.starColor,
      // Deliberately coarse: the map should say "that one has oceans", not
      // show the coastlines. Detail is what makes flying there worth it.
      quality: 'low',
    });
    animatedMaterials.push(planetMaterial);

    const body = new THREE.Mesh(new THREE.SphereGeometry(planet.radius, 32, 24), planetMaterial);
    pivot.add(body);

    for (const moon of planet.moons) {
      drawOrbitPath(moon.orbit, pivot, 0.14);

      const moonMesh = new THREE.Mesh(
        new THREE.SphereGeometry(moon.radius, 14, 10),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color(moon.baseColor),
          roughness: 0.95,
        }),
      );
      track(moonMesh, moon.orbit, pivot);
      bodyNodes.set(moon.name, moonMesh);
    }

    labelTargets.push({ text: planet.name, node: pivot, lift: planet.radius + 120 });
    bodyNodes.set(planet.name, pivot);
  }

  /** Where a body hangs: its host planet's pivot, or the system root. */
  const frameFor = (parent: string | null): THREE.Object3D =>
    (parent && planetPivots.get(parent)) || group;

  // Stations — boxes, coloured by who holds them.
  for (const station of system.stations) {
    const frame = frameFor(station.parent);
    drawOrbitPath(station.orbit, frame, 0.12);

    // Map detail only: the deck's footprint, extruded to its thickness. Pads,
    // markings and surface clutter are all generated, and all deliberately left
    // undrawn here — from across a system you should see that a station exists
    // and how large it is, not be able to count its berths from orbit.
    //
    // Drawn at chart scale, not true scale. A station is genuinely a speck next
    // to a world, and at true size it comes out under a pixel from map range —
    // which would mean the map simply stopped showing stations at all. So the
    // deck is drawn larger than life, the way a chart marks a town with a dot
    // far bigger than the town. Ratios between stations are untouched, so
    // "bigger deck means more berths" still reads.
    const geometry = getStationGeometry(seed, station);
    const chart = MAP_STATION_EXAGGERATION / STATION_SCALE;
    const shape = new THREE.Shape(
      geometry.outline.map((point) => new THREE.Vector2(point.x * chart, point.y * chart)),
    );
    const deckGeometry = new THREE.ExtrudeGeometry(shape, {
      depth: geometry.thickness * chart,
      bevelEnabled: false,
    });
    deckGeometry.translate(0, 0, (-geometry.thickness * chart) / 2);

    const deck = new THREE.Mesh(
      deckGeometry,
      new THREE.MeshStandardMaterial({
        color: ALLEGIANCE_COLORS[station.allegiance ?? 'unaligned'],
        roughness: 0.55,
        metalness: 0.4,
      }),
    );
    // A deck is a plate: it should read as lying in its orbital plane rather
    // than standing on edge.
    deck.rotation.z = station.orbit.argumentOfPeriapsis;
    track(deck, station.orbit, frame);

    // Whose station this is, on the top face — the only face a chart ever
    // shows. Allegiance is already in the deck's colour, but a colour only
    // reads if you know the palette, and knowing who owns a station before
    // committing to a jump is exactly what a map is for.
    if (station.allegiance) {
      const size = geometry.radius * chart * 1.1;
      const badge = new THREE.Mesh(
        new THREE.PlaneGeometry(size, size),
        new THREE.MeshBasicMaterial({
          map: factionTexture(station.allegiance),
          transparent: true,
          depthWrite: false,
          opacity: 0.95,
        }),
      );
      badge.position.z = (geometry.thickness * chart) / 2 + 0.01;
      deck.add(badge);
    }

    const size = geometry.radius * chart;

    // Counted per class, e.g. "3S 2M 1L". Listing only the classes present made
    // a station with four small pads read as though it had one.
    const pads = SIZE_CLASSES.map((size) => {
      const n = station.pads.filter((p) => p.size === size).length;
      return n > 0 ? `${n}${PAD_LABELS[size]}` : null;
    })
      .filter(Boolean)
      .join(' ');
    labelTargets.push({ text: `${station.name} [${pads}]`, node: deck, lift: size + 110 });
    bodyNodes.set(station.name, deck);
  }

  // Asteroid fields. A belt spans its whole orbit, so it is drawn *as* the
  // orbit — a thick ring — rather than as an object sitting somewhere on one.
  for (const field of system.asteroidFields) {
    const frame = frameFor(field.parent);

    // Both kinds are rings — the difference is only what they ring. A belt
    // circles the star; a field circles its planet, close in, like Saturn's.
    // Several offset loops read as a band of debris rather than as a wire.
    const bands = field.kind === 'belt' ? 5 : 4;
    for (let i = 0; i < bands; i++) {
      const spread = (i / (bands - 1) - 0.5) * field.radius;
      frame.add(
        lineLoop(
          orbitPath({ ...field.orbit, semiMajorAxis: field.orbit.semiMajorAxis + spread }, 96)
            .map((p) => new THREE.Vector3(p.x, p.y, p.z)),
          0xb0a08a,
          field.kind === 'belt' ? 0.3 : 0.42,
        ),
      );
    }

    labelTargets.push({
      text: field.name,
      node: frame,
      lift: field.orbit.semiMajorAxis + field.radius,
      muted: true,
    });
  }

  // Anomalies — octahedra, so they read as neither planet nor station.
  for (const anomaly of system.anomalies) {
    drawOrbitPath(anomaly.orbit, group, 0.1);

    const marker = new THREE.Mesh(
      new THREE.OctahedronGeometry(80),
      new THREE.MeshStandardMaterial({ color: 0xc98bff, emissive: 0x3a1a55, roughness: 0.4 }),
    );
    track(marker, anomaly.orbit, group);
    labelTargets.push({
      text: `${anomaly.name} (${anomaly.kind})`,
      node: marker,
      lift: 190,
      muted: true,
    });
    bodyNodes.set(anomaly.name, marker);
  }

  const animate = (seconds: number): void => {
    for (const { node, orbit } of moving) {
      const at = positionAt(orbit, seconds);
      node.position.set(at.x, at.y, at.z);
    }
    for (const material of animatedMaterials) {
      material.uniforms['uTime']!.value = seconds;
    }
  };
  animate(0);

  // Labels follow their body, so they are resolved from live world positions
  // each frame rather than baked at build time.
  const labels = labelTargets.map(({ text, node, lift, muted }) => ({
    text,
    position: new THREE.Vector3(),
    muted,
    follow: () => {
      node.getWorldPosition(scratchWorld);
      return scratchWorld.clone().add(new THREE.Vector3(0, 0, lift * SYSTEM_RENDER_SCALE));
    },
  }));

  return {
    group: outer,
    pickables,
    labels,
    animate,
    bodies: { frame: group, byName: bodyNodes },
    dispose: () => disposeGroup(outer),
  };
}

const scratchWorld = new THREE.Vector3();

/** Summary line for whichever tier is in focus. */
export function describeSystem(system: ReturnType<typeof getSystem>): string[] {
  return [
    `${system.planets.length} planets`,
    `${system.stations.length} stations`,
    `${system.asteroidFields.length} asteroid fields`,
    `${system.anomalies.length} anomalies`,
  ];
}
